import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  Divider, 
  Button, 
  List,
  ListItem,
  ListItemText,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { safeApiCall, resetNotificationSettings, getNotificationDebugInfo, initializePushNotifications, getPushNotificationStatus } from '../utils/api';
import { 
  IoNotificationsOutline, 
  IoDownloadOutline, 
  IoPhonePortraitOutline 
} from 'react-icons/io5';

function Settings() {
  const navigate = useNavigate();
  const { currentUser, logout, userRole } = useAuth();
  const { 
    subscribeToPush, 
    unsubscribeFromPush, 
    isInstallable, 
    installPWA,
    getPWAStatus,
    realtimeConnected
  } = useNotification();
  
  const [loading, setLoading] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    push_enabled: false,
    likes: true,
    comments: true,
    messages: true,
    mentions: true
  });
  const [pushSupported, setPushSupported] = useState(false);
  const [pwaStatus, setPwaStatus] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // プッシュ通知のサポート状況をチェック
  useEffect(() => {
    const checkPushSupport = () => {
      const supported = 'serviceWorker' in navigator && 
                       'PushManager' in window && 
                       'Notification' in window;
      setPushSupported(supported);
    };
    
    checkPushSupport();
  }, []);

  // 通知設定とPWAステータスを取得
  useEffect(() => {
    const fetchSettings = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      try {
        // 通知設定を取得
        const notificationResponse = await safeApiCall.get('/api/notifications/settings');
        if (notificationResponse.data) {
          setNotificationSettings({
            push_enabled: Boolean(notificationResponse.data.push_enabled),
            likes: notificationResponse.data.likes !== undefined ? Boolean(notificationResponse.data.likes) : true,
            comments: notificationResponse.data.comments !== undefined ? Boolean(notificationResponse.data.comments) : true,
            messages: notificationResponse.data.messages !== undefined ? Boolean(notificationResponse.data.messages) : true,
            mentions: notificationResponse.data.mentions !== undefined ? Boolean(notificationResponse.data.mentions) : true
          });
        } else {
          // デフォルト設定を設定
          setNotificationSettings({
            push_enabled: false,
            likes: true,
            comments: true,
            messages: true,
            mentions: true
          });
        }

        // PWAステータスを取得
        try {
          const pwaResponse = await getPWAStatus();
          if (pwaResponse && pwaResponse.status === 'success') {
            setPwaStatus(pwaResponse.pwaStatus);
          }
        } catch (pwaError) {
          console.log('PWA status fetch failed:', pwaError);
        }
      } catch (error) {
        console.error('設定の取得に失敗:', error);
        showNotification('設定の取得に失敗しました', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, [currentUser, getPWAStatus]);

  // プッシュ通知の有効/無効切り替え
  const handlePushToggle = async (enabled) => {
    setLoading(true);
    try {
      if (enabled) {
        // プッシュ通知を有効にする
        if (!pushSupported) {
          showNotification('お使いのブラウザはプッシュ通知をサポートしていません', 'error');
          setLoading(false);
          return;
        }

        // ブラウザの通知許可をリクエスト
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          showNotification('通知の許可が拒否されました', 'error');
          setLoading(false);
          return;
        }

        // プッシュ通知を購読
        const subscribed = await subscribeToPush();
        if (!subscribed) {
          showNotification('プッシュ通知の購読に失敗しました', 'error');
          setLoading(false);
          return;
        }
      } else {
        // プッシュ通知を無効にする
        await unsubscribeFromPush();
      }

      // サーバーに設定を保存
      await saveNotificationSettings({ push_enabled: enabled });
      setNotificationSettings(prev => ({ ...prev, push_enabled: enabled }));
      
      showNotification(
        enabled ? 'プッシュ通知を有効にしました' : 'プッシュ通知を無効にしました', 
        'success'
      );
    } catch (error) {
      console.error('プッシュ通知設定の変更に失敗:', error);
      showNotification('設定の変更に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 通知設定の変更
  const handleSettingChange = (setting) => async (event) => {
    const newValue = event.target.checked;
    
    if (setting === 'push_enabled') {
      await handlePushToggle(newValue);
      return;
    }

    try {
      await saveNotificationSettings({ [setting]: newValue });
      setNotificationSettings(prev => ({ ...prev, [setting]: newValue }));
      showNotification('通知設定を更新しました', 'success');
    } catch (error) {
      console.error('通知設定の更新に失敗:', error);
      showNotification('設定の更新に失敗しました', 'error');
    }
  };

  // 設定をサーバーに保存
  const saveNotificationSettings = async (settings) => {
    try {
      const response = await safeApiCall.post('/api/notifications/settings', settings);
      if (response.data && response.data.status === 'success') {
        return true;
      } else {
        throw new Error(response.data?.message || '設定の保存に失敗しました');
      }
    } catch (error) {
      console.error('設定保存エラー:', error);
      throw error;
    }
  };

  // テスト通知を送信
  const handleTestNotification = async () => {
    if (!notificationSettings.push_enabled) {
      showNotification('プッシュ通知を有効にしてからテストしてください', 'warning');
      return;
    }

    setLoading(true);
    try {
      // FCM 経由でテスト通知を送信（SSE は廃止済み）
      const response = await safeApiCall.post('/api/notifications/test-fcm', {
        title: 'テスト通知',
        message: 'FCM経由のプッシュ通知テストです',
        timestamp: new Date().toISOString()
      });

      if (response.data && response.data.status === 'success') {
        showNotification('FCM経由でテスト通知を送信しました', 'success');
      } else {
        // トークン未登録などのサーバー側メッセージを表示
        showNotification(response.data?.message || 'テスト通知の送信に失敗しました', 'error');
      }
    } catch (error) {
      console.error('テスト通知送信エラー:', error);
      showNotification(error?.response?.data?.message || 'テスト通知の送信に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 非同期プッシュ通知テスト
  const handleTestAsyncNotification = async () => {
    if (!notificationSettings.push_enabled) {
      showNotification('プッシュ通知を有効にしてからテストしてください', 'warning');
      return;
    }

    setLoading(true);
    try {
      // SSE経由で非同期テスト通知を送信
      const response = await safeApiCall.post('/api/notifications/test-sse-async', {
        type: 'test_notification_async',
        data: {
          title: '非同期テスト通知',
          message: 'SSE経由の非同期プッシュ通知テストです',
          timestamp: new Date().toISOString()
        }
      });
      
      if (response.data && response.data.status === 'success') {
        showNotification('非同期テスト通知をSSE経由で送信しました', 'success');
        console.log('🔔 SSE経由の非同期プッシュ通知テスト結果:', response.data);
      } else {
        showNotification('非同期テスト通知の送信に失敗しました', 'error');
      }
    } catch (error) {
      console.error('非同期テスト通知送信エラー:', error);
      showNotification('非同期テスト通知の送信に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // プッシュ通知システムの詳細ダッシュボードを表示
  const handleShowPushDashboard = async () => {
    setLoading(true);
    try {
      const response = await safeApiCall.get('/api/notifications/dashboard');
      
      if (response.data && response.data.status === 'success') {
        console.log('📊 プッシュ通知ダッシュボード:', response.data.dashboard);
        
        const dashboard = response.data.dashboard;
        let message = `システム状況:\n`;
        message += `- プッシュ通知有効: ${dashboard.system?.push_notifications_enabled}\n`;
        message += `- ワーカー動作中: ${dashboard.system?.async_worker_running}\n`;
        message += `- キューサイズ: ${dashboard.system?.queue_size}\n`;
        message += `- ユーザー購読数: ${dashboard.user_subscriptions?.count}\n`;
        message += `- VAPID設定: ${dashboard.vapid?.private_key_configured && dashboard.vapid?.public_key_configured ? '完了' : '未完了'}\n`;
        
        if (dashboard.recommendations && dashboard.recommendations.length > 0) {
          message += `\n推奨アクション:\n${dashboard.recommendations.join('\n')}`;
        }
        
        alert(message);
        showNotification('ダッシュボード情報をコンソールに出力しました', 'info');
      } else {
        showNotification('ダッシュボード情報の取得に失敗しました', 'error');
      }
    } catch (error) {
      console.error('ダッシュボード取得エラー:', error);
      showNotification('ダッシュボード情報の取得に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ブラウザのプッシュ通知状況を詳細チェック
  const handleCheckBrowserStatus = async () => {
    console.log('🔍 === ブラウザプッシュ通知状況チェック ===');
    
    // 基本的な権限チェック
    console.log('🔍 Notification.permission:', Notification.permission);
    console.log('🔍 navigator.serviceWorker:', !!navigator.serviceWorker);
    console.log('🔍 window.PushManager:', !!window.PushManager);
    
    let message = `ブラウザ状況:\n`;
    message += `- 通知権限: ${Notification.permission}\n`;
    message += `- Service Worker対応: ${!!navigator.serviceWorker}\n`;
    message += `- Push Manager対応: ${!!window.PushManager}\n`;
    
    // Service Worker登録状況
    if (navigator.serviceWorker) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        console.log('🔍 Service Worker registration:', registration);
        message += `- SW登録状況: ${registration ? '登録済み' : '未登録'}\n`;
        
        if (registration) {
          console.log('🔍 SW state:', registration.active?.state);
          message += `- SW状態: ${registration.active?.state || 'unknown'}\n`;
          
          // プッシュ購読状況
          try {
            const subscription = await registration.pushManager.getSubscription();
            console.log('🔍 Push subscription:', subscription);
            message += `- プッシュ購読: ${subscription ? '購読済み' : '未購読'}\n`;
            
            if (subscription) {
              console.log('🔍 Subscription endpoint:', subscription.endpoint);
              message += `- エンドポイント: ${subscription.endpoint.substring(0, 50)}...\n`;
            }
          } catch (subError) {
            console.error('🔍 Push subscription check error:', subError);
            message += `- プッシュ購読チェックエラー: ${subError.message}\n`;
          }
        }
      } catch (swError) {
        console.error('🔍 Service Worker check error:', swError);
        message += `- SWチェックエラー: ${swError.message}\n`;
      }
    }
    
    alert(message);
    console.log('🔍 === ブラウザチェック完了 ===');
    showNotification('ブラウザ状況をコンソールに出力しました', 'info');
  };

  // ワーカーの詳細状況を確認
  const handleCheckWorkerStatus = async () => {
    setLoading(true);
    try {
      const response = await safeApiCall.get('/api/notifications/worker-status');
      
      if (response.data && response.data.status === 'success') {
        console.log('🔧 ワーカー詳細状況:', response.data.worker_status);
        
        const status = response.data.worker_status;
        let message = `ワーカー詳細状況:\n`;
        message += `- ワーカー動作中: ${status.worker_running}\n`;
        message += `- キューサイズ: ${status.queue_info.size}\n`;
        message += `- キュー空: ${status.queue_info.empty}\n`;
        message += `- アクティブスレッド数: ${status.thread_count}\n`;
        message += `- スレッド詳細: ${status.threads.length}個のスレッド\n`;
        
        status.threads.forEach((thread, index) => {
          message += `  ${index + 1}. ${thread.name} (alive: ${thread.alive}, daemon: ${thread.daemon})\n`;
        });
        
        alert(message);
        showNotification('ワーカー状況をコンソールに出力しました', 'info');
      } else {
        showNotification('ワーカー状況の取得に失敗しました', 'error');
      }
    } catch (error) {
      console.error('ワーカー状況取得エラー:', error);
      showNotification('ワーカー状況の取得に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ワーカーを再起動
  const handleRestartWorker = async () => {
    if (!window.confirm('プッシュワーカーを再起動しますか？')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await safeApiCall.post('/api/notifications/restart-worker');
      
      if (response.data && response.data.status === 'success') {
        showNotification('プッシュワーカーを再起動しました', 'success');
        console.log('🔄 ワーカー再起動結果:', response.data);
      } else {
        showNotification('ワーカーの再起動に失敗しました', 'error');
      }
    } catch (error) {
      console.error('ワーカー再起動エラー:', error);
      showNotification('ワーカーの再起動に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 同期プッシュ通知テスト（CGI環境対応）
  const handleTestSyncPush = async () => {
    if (!notificationSettings.push_enabled) {
      showNotification('プッシュ通知を有効にしてからテストしてください', 'warning');
      return;
    }

    setLoading(true);
    try {
      const response = await safeApiCall.post('/api/notifications/test-sync-push');
      
      if (response.data && response.data.status === 'success') {
        showNotification('同期プッシュ通知を送信しました', 'success');
        console.log('🔔 同期プッシュ通知テスト結果:', response.data);
      } else if (response.data && response.data.status === 'warning') {
        showNotification(response.data.message, 'warning');
        console.log('⚠️ 同期プッシュ通知テスト結果:', response.data);
      } else {
        showNotification('同期プッシュ通知の送信に失敗しました', 'error');
      }
    } catch (error) {
      console.error('同期プッシュ通知送信エラー:', error);
      showNotification('同期プッシュ通知の送信に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // キューを強制処理
  const handleProcessQueue = async () => {
    if (!window.confirm('キューに残っている通知を強制的に処理しますか？')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await safeApiCall.post('/api/notifications/process-queue');
      
      if (response.data && response.data.status === 'success') {
        const { processed_count, errors, remaining_queue_size } = response.data;
        let message = `キューを処理しました（処理数: ${processed_count}`;
        if (errors.length > 0) {
          message += `, エラー: ${errors.length}`;
        }
        message += `, 残り: ${remaining_queue_size}）`;
        
        showNotification(message, errors.length > 0 ? 'warning' : 'success');
        console.log('🔄 キュー処理結果:', response.data);
      } else {
        showNotification('キューの処理に失敗しました', 'error');
      }
    } catch (error) {
      console.error('キュー処理エラー:', error);
      showNotification('キューの処理に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // サーバー基本テスト
  const handleServerTest = async () => {
    setLoading(true);
    try {
      console.log('🧪 サーバーテスト開始...');
      const response = await safeApiCall.get('/api/notifications/server-test');
      
      if (response.data && response.data.status === 'success') {
        showNotification('サーバーテスト成功', 'success');
        console.log('🧪 サーバーテスト結果:', response.data);
      } else {
        showNotification('サーバーテストに失敗しました', 'error');
        console.log('🧪 サーバーテスト失敗:', response.data);
      }
    } catch (error) {
      console.error('🧪 サーバーテストエラー:', error);
      showNotification('サーバーテストでエラーが発生しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 通知設定をリセット
  const handleResetNotificationSettings = async () => {
    try {
      setLoading(true);
      const response = await resetNotificationSettings();
      if (response.status === 'success') {
        // 設定を更新
        setNotificationSettings({
          push_enabled: true,
          likes: true,
          comments: true,
          messages: true,
          mentions: true
        });
        showNotification('通知設定をリセットしました', 'success');
      } else {
        showNotification('通知設定のリセットに失敗しました', 'error');
      }
    } catch (error) {
      console.error('通知設定リセットエラー:', error);
      showNotification('通知設定のリセットに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  // デバッグ情報を表示
  const showDebugInfo = async () => {
    try {
      console.log('=== プッシュ通知デバッグ情報 ===');
      
      // プッシュ通知ステータスを確認
      try {
        const status = await getPushNotificationStatus();
        console.log('📊 プッシュ通知ステータス:', status);
      } catch (statusError) {
        console.error('📊 ステータス取得エラー:', statusError);
      }
      
      // ユーザー設定を確認
      try {
        const debugInfo = await getNotificationDebugInfo();
        console.log('🔍 ユーザー設定デバッグ情報:', debugInfo);
      } catch (debugError) {
        console.error('🔍 ユーザー設定取得エラー:', debugError);
      }
      
      showNotification('デバッグ情報をコンソールに出力しました', 'info');
    } catch (error) {
      console.error('デバッグ情報取得エラー:', error);
      showNotification('デバッグ情報の取得に失敗しました', 'error');
    }
  };

  // プッシュ通知機能は無効化中
  const handleInitializePush = async () => {
    showNotification('プッシュ通知機能は現在無効化中です', 'info');
  };

  // PWAインストールのハンドラー
  const handleInstallPWA = async () => {
    try {
      const success = await installPWA();
      if (success) {
        showNotification('アプリをインストールしました！', 'success');
      } else {
        showNotification('インストールをキャンセルしました', 'info');
      }
    } catch (error) {
      console.error('PWAインストールエラー:', error);
      showNotification('インストールに失敗しました', 'error');
    }
  };

  // アカウント削除のハンドラー（閲覧ユーザーのみ）
  const handleDeleteAccount = async () => {
    if (window.confirm('本当にアカウントを削除しますか？この操作は取り消せません。')) {
      setLoading(true);
      try {
        const response = await safeApiCall.delete('/api/users/delete-account');
        
        if (response.data && response.data.status === 'success') {
          showNotification('アカウントが削除されました', 'success');
          setTimeout(() => {
            logout();
            navigate('/login');
          }, 2000);
        } else {
          showNotification('アカウントの削除に失敗しました', 'error');
          setLoading(false);
        }
      } catch (error) {
        console.error('アカウント削除エラー:', error);
        showNotification('アカウントの削除に失敗しました', 'error');
        setLoading(false);
      }
    }
  };

  // 通知表示
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // 通知を閉じる
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // PWA vs デスクトップブラウザ診断
  const handlePWAVsBrowserDiagnostic = async () => {
    console.log('🔍 === PWA vs デスクトップブラウザ診断 ===');
    
    // PWAモード判定
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                 window.navigator.standalone ||
                 document.referrer.includes('android-app://');
    
    // ブラウザ種別判定
    const isChrome = !!window.chrome;
    const isFirefox = navigator.userAgent.includes('Firefox');
    const isEdge = navigator.userAgent.includes('Edg');
    const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
    
    let message = `診断結果:\n`;
    message += `📱 動作モード: ${isPWA ? 'PWAモード' : 'ブラウザタブモード'}\n`;
    message += `🌐 ブラウザ: ${isChrome ? 'Chrome' : isFirefox ? 'Firefox' : isEdge ? 'Edge' : isSafari ? 'Safari' : 'その他'}\n`;
    message += `🔔 通知権限: ${Notification.permission}\n`;
    
    // Service Worker状況
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        message += `⚙️ Service Worker: ${registration ? '登録済み' : '未登録'}\n`;
        
        if (registration) {
          message += `📍 SWスコープ: ${registration.scope}\n`;
          message += `🔄 SW状態: ${registration.active?.state || 'unknown'}\n`;
          
          // プッシュ購読状況
          try {
            const subscription = await registration.pushManager.getSubscription();
            message += `📡 プッシュ購読: ${subscription ? '購読済み' : '未購読'}\n`;
          } catch (pushError) {
            message += `📡 プッシュ購読: エラー - ${pushError.message}\n`;
          }
        }
      } catch (swError) {
        message += `⚙️ Service Worker: エラー - ${swError.message}\n`;
      }
    } else {
      message += `⚙️ Service Worker: 未対応\n`;
    }
    
    // 推奨事項
    message += `\n💡 推奨事項:\n`;
    if (!isPWA && (isChrome || isEdge)) {
      message += `- PWAとしてインストールすることで、より安定した通知機能が利用できます\n`;
      message += `- アドレスバーの右端のインストールアイコンをクリック\n`;
    }
    
    if (Notification.permission !== 'granted') {
      message += `- ブラウザ設定で通知を許可してください\n`;
    }
    
    if (!isPWA && isSafari) {
      message += `- Safari（デスクトップ）では、PWAとしてDockに追加後に通知機能が有効になります\n`;
    }
    
    alert(message);
    console.log('🔍 診断完了');
  };

  if (!currentUser) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            ログインが必要です
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/login')}
          >
            ログイン
          </Button>
        </Paper>
      </Container>
    );
  }

  // 閲覧ユーザー（viewer）かどうかをチェック
  const isViewerUser = userRole === 'viewer' || currentUser?.role === 'viewer';

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IoNotificationsOutline size={28} style={{ marginRight: '12px' }} />
          <Typography variant="h5">
            通知・設定
          </Typography>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* PWAインストール設定 */}
            {isInstallable && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <IoPhonePortraitOutline style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    アプリインストール
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    ホーム画面にアプリを追加して、よりアプリらしい体験をお楽しみください。
                    オフライン機能やプッシュ通知など、より充実した機能が利用できます。
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<IoDownloadOutline />}
                    onClick={handleInstallPWA}
                    disabled={loading}
                    sx={{ mb: 1 }}
                  >
                    ホーム画面に追加
                  </Button>
                  {pwaStatus && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" display="block" color="text.secondary">
                        PWA機能: {pwaStatus.features?.backgroundSync ? '背景同期 ✓ ' : ''}
                        {pwaStatus.features?.badgeApi ? 'バッジ表示 ✓ ' : ''}
                        {pwaStatus.features?.offlineCache ? 'オフラインキャッシュ ✓' : ''}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* プッシュ通知設定 */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <IoNotificationsOutline style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  プッシュ通知設定
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="プッシュ通知" 
                      secondary={
                        pushSupported 
                          ? "ブラウザでプッシュ通知を受け取る" 
                          : "お使いのブラウザはプッシュ通知をサポートしていません"
                      }
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notificationSettings.push_enabled}
                          onChange={handleSettingChange('push_enabled')}
                          color="primary"
                          disabled={!pushSupported || loading}
                        />
                      }
                      label=""
                    />
                  </ListItem>
                  
                  {/* リアルタイム更新状態 */}
                  {/* 
                  <ListItem>
                    <ListItemText 
                      primary="⚡ リアルタイム更新" 
                      secondary={
                        <Box>
                          <Typography variant="body2" component="div">
                            {realtimeConnected ? (
                              <span style={{ color: '#4caf50', fontWeight: 'bold' }}>
                                🟢 接続中 - プッシュ通知ベースのリアルタイム更新が有効
                              </span>
                            ) : (
                              <span style={{ color: '#ff9800', fontWeight: 'bold' }}>
                                🔴 切断 - スマートポーリング（30秒間隔）で動作中
                              </span>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            🚀 <strong>パフォーマンス改善:</strong> 従来の20秒間隔ポーリングから解放され、サーバーリクエストを90%以上削減
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  */}
                  
                  {notificationSettings.push_enabled && (
                    <>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="いいね通知" 
                          secondary="投稿にいいねがついた時に通知" 
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notificationSettings.likes}
                              onChange={handleSettingChange('likes')}
                              color="primary"
                              disabled={loading}
                            />
                          }
                          label=""
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="コメント通知" 
                          secondary="投稿にコメントがついた時に通知" 
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notificationSettings.comments}
                              onChange={handleSettingChange('comments')}
                              color="primary"
                              disabled={loading}
                            />
                          }
                          label=""
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="メッセージ通知" 
                          secondary="新しいメッセージを受信した時に通知" 
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notificationSettings.messages}
                              onChange={handleSettingChange('messages')}
                              color="primary"
                              disabled={loading}
                            />
                          }
                          label=""
                        />
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemText 
                          primary="メンション通知" 
                          secondary="@メンションされた時に通知" 
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notificationSettings.mentions}
                              onChange={handleSettingChange('mentions')}
                              color="primary"
                              disabled={loading}
                            />
                          }
                          label=""
                        />
                      </ListItem>
                    </>
                  )}
                </List>
                
                <Box sx={{ mt: 2, textAlign: 'center', display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {notificationSettings.push_enabled && (
                    <Button
                      variant="outlined"
                      onClick={handleTestNotification}
                      disabled={loading}
                    >
                      テスト通知を送信
                    </Button>
                  )}
                  {/* 
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={handleResetNotificationSettings}
                    disabled={loading}
                  >
                    設定をリセット
                  </Button>
                  <Button
                    variant="outlined"
                    color="info"
                    onClick={showDebugInfo}
                    disabled={loading}
                  >
                    デバッグ情報表示
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleInitializePush}
                    disabled={loading}
                  >
                    プッシュ機能初期化
                  </Button>
                  */}
                </Box>
              </CardContent>
            </Card>
            
            {/* アカウント削除（閲覧ユーザーのみ） */}
            {isViewerUser && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="error">
                    危険な操作
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteAccount}
                    disabled={loading}
                  >
                    アカウントを削除
                  </Button>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    ※ アカウントを削除すると、すべてのデータが永久に削除されます。この操作は取り消せません。
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Paper>
      
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Settings; 